import { FieldSet, FormattedString, InputWizardPanelComponent, SelectComponent, TextInput } from "o4a-react";
import * as React from "react";
import { FontIcon, mergeStyleSets } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { RegionSelect } from "../../components/RegionSelect/RegionSelect";
import { ResourceGroupSelect } from "../../components/ResourceGroupSelect/ResourceGroupSelect";
import { SubscriptionSelect } from "../../components/SubscriptionSelect/SubscriptionSelect";
import { adbsBasicTabLearnMoreUrl } from "../../constants/docConstants";
import { SvgIconIds } from "../../constants/uiConstants";
import { nameCharMaxLength, nameCharMinLength, nameValidationRegex } from "../../helpers/validationHelper";

export const GROUP_BASICS = "basics";

export enum Fields {
  Subscription = "subscriptionId",
  ResourceGroup = "resourceGroupId",
  Name = "adbsDatabaseDetails.name",
  Region = "region",
}

export enum FieldTestIds {
  Name = "name",
  ResourceGroup = "resource-group",
  Subscription = "subscription",
  Region = "region"
}

export interface BasicsTabComponent {
  closeSidePanels: () => void;
}
export interface BasicsTabContentProps {
  location?: string;
  subscription?: string;
  resourceGroupId?: string;
  inputWizardPanelRef: InputWizardPanelComponent;
}

const classNames = mergeStyleSets({
  featureHeader: { paddingBottom: "15px" },
  punchOutLinkIcon: { paddingLeft: "5px", width: "12px", height: "12px" },
});

export const BasicsTabContent = (
  {
    location, subscription, resourceGroupId,
    inputWizardPanelRef,
  }: BasicsTabContentProps,
): JSX.Element => {
  const [subscriptionId, setSubscriptionId] = React.useState<string>(subscription || "");

  const resourceSelectRef = React.useRef<SelectComponent>();
  const setResourceSelectRef = (selectComponent: SelectComponent): void => {
    resourceSelectRef.current = selectComponent;
  };

  const onSubscriptionChange = (subId: string): void => setSubscriptionId(subId);

  const onResourceGroupError = (): void => {
    inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.resourceGroups());
  };

  const onResourceGroupMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.message(),
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.title(),
  );

  return (
    <>
      <div className={classNames.featureHeader}>
        {FormattedString(
          { inputText: Messages.createAdbs.basicsTab.info.learnMore(adbsBasicTabLearnMoreUrl) },
        )}
        <FontIcon
          iconName={SvgIconIds.punchOutIconSvg}
          className={classNames.punchOutLinkIcon}
        />
      </div>
      <FieldSet header={Messages.createAdbs.basicsTab.sectionTitles.projectDetails()}>
        <SubscriptionSelect
          required
          onChange={onSubscriptionChange}
          testId={FieldTestIds.Subscription}
          fieldName={Fields.Subscription}
          label={Messages.labels.subscription()}
          tooltip={Messages.hints.toolTipSubscription()}
          defaultValue={subscriptionId ? [subscriptionId] : undefined}
        />
        <ResourceGroupSelect
          required
          subField
          testId={FieldTestIds.ResourceGroup}
          subscriptionId={subscriptionId}
          componentRef={setResourceSelectRef}
          fieldName={Fields.ResourceGroup}
          label={Messages.labels.resourceGroup()}
          tooltip={Messages.hints.toolTipResourceGroup()}
          onError={onResourceGroupError}
          onMissingDependencies={onResourceGroupMissingDependencies}
          defaultValue={resourceGroupId ? [resourceGroupId] : undefined}
        />
      </FieldSet>
      <FieldSet header={Messages.createAdbs.basicsTab.sectionTitles.instanceDetails()}>
        <TextInput
          required
          testId={FieldTestIds.Name}
          fieldName={Fields.Name}
          label={Messages.labels.name()}
          validator={(value: string | undefined) => {
            const errors: string[] = [];
            if (!value || (value?.length < nameCharMinLength || value?.length > nameCharMaxLength)) {
              errors.push(Messages.validation.nameChars(nameCharMinLength.toString(), nameCharMaxLength.toString()));
            }
            if (!value || (value && !nameValidationRegex.test(value))) {
              errors.push(Messages.validation.nameValidation());
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        <RegionSelect
          required
          subscriptionId={subscriptionId}
          testId={FieldTestIds.Region}
          // showUnlinkedRegions
          fieldName={Fields.Region}
          label={Messages.labels.region()}
          tooltip={Messages.hints.toolTipRegion()}
          defaultValue={location ? [location] : undefined}
        />
      </FieldSet>
    </>
  );
};
